import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import {Icon} from '@edna/components/primitives';
import KeyboardArrowRightIcon from '@edna/icons/keyboardArrowRight.svg';

import {TableHead} from 'src/components/List';
import {DEFAULT_DEBOUNCE_DELAY, EChannelType, TItemId} from 'src/constants';
import {useGetApprovedSubjectsQuery} from 'src/containers/Subjects/api';
import {
  TAvailableTariffResponse,
  useGetAvailableTariffsQuery,
  useGetConnectedTariffsQuery,
} from 'src/containers/Tariffs';
import {EPermission, usePermission} from 'src/containers/User';
import {useToggle} from 'src/hooks';

import {useGetAllLicensesQuery} from '../api';
import {ELicenseStatus} from '../definitions';
import Item from './Item';
import {LICENSE_CHANNELS, TSubjectLicense} from './Item/definitions';
import * as S from './style';

type TProps = {
  className?: string;
  tenantId?: TItemId;
};

const DEFAULT_SHOWING_ITEMS = 5;

type TTariffById = Record<number, TAvailableTariffResponse>;

const useTariffsBySubject = (tenantId?: TItemId) => {
  const {data: tariffs = [], fulfilledTimeStamp: tariffsTimeStamp} = useGetAvailableTariffsQuery({
    tenantId,
  });
  const {data: connectedTariffs, fulfilledTimeStamp: subjectsTimeStamp} =
    useGetConnectedTariffsQuery(tenantId, {
      skip: !tariffs.length,
    });

  const tariffsBySubject = React.useMemo<TTariffById>(() => {
    if (!connectedTariffs || !tariffs.length) {
      return {};
    }

    const tariffsById = tariffs.reduce<TTariffById>(
      (result, tariff) => ({
        ...result,
        [tariff.active.id]: tariff,
      }),
      {},
    );

    return connectedTariffs.reduce<TTariffById>((result, tariff) => {
      return {...result, [tariff.active.subjectId]: tariffsById[tariff.active.selectedTariffId]};
    }, {});
  }, [tariffsTimeStamp, subjectsTimeStamp]);

  return tariffsBySubject;
};

const SCROLL_GAP = 15;

const VerticalScrollTable = React.memo<{children: React.ReactNode; columnsWidth: string[]}>(
  ({children, columnsWidth}) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [isLeftTableSide, setIsLeftTableSide] = React.useState(true);
    const [hasSlider, setHasSlider] = React.useState(false);

    const handleScroll = React.useMemo(
      () =>
        debounce(() => {
          if (!contentRef.current) {
            return;
          }

          if (contentRef.current.scrollLeft === 0) {
            return setIsLeftTableSide(true);
          }

          const offsetLeft =
            contentRef.current.scrollWidth - contentRef.current.offsetWidth - SCROLL_GAP;

          if (contentRef.current.scrollLeft >= offsetLeft) {
            return setIsLeftTableSide(false);
          }
        }, DEFAULT_DEBOUNCE_DELAY / 2),
      [],
    );

    const handleResize = React.useMemo(
      () =>
        debounce(() => {
          if (!contentRef.current) {
            return;
          }

          setHasSlider(
            contentRef.current.scrollWidth - SCROLL_GAP > contentRef.current.offsetWidth,
          );
        }, DEFAULT_DEBOUNCE_DELAY / 2),
      [],
    );

    const toggleTableSide = () => {
      if (!contentRef.current) {
        return;
      }

      contentRef.current.scrollTo({
        left: isLeftTableSide ? contentRef.current.offsetWidth : 0,
        behavior: 'smooth',
      });
    };

    React.useEffect(() => {
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect(() => {
      handleResize();
    }, [columnsWidth]);

    return (
      <S.TableWrapper>
        <S.TableContent columnsWidth={columnsWidth} ref={contentRef} onScroll={handleScroll}>
          {children}
        </S.TableContent>
        {hasSlider && (
          <S.Slider onClick={toggleTableSide} toRight={isLeftTableSide}>
            <Icon as={KeyboardArrowRightIcon} size="24px" />
          </S.Slider>
        )}
      </S.TableWrapper>
    );
  },
);

const TABLE_CELLS = ['280px', '140px', '220px', '144px', '200px'];
const TABLE_TARIFF_CELL = '100px';
const TABLE_WA_CELLS = '120px';

const LicensesList = React.memo<TProps>(({className, ...props}) => {
  const {id: supportTenantId} = useParams();
  const {t} = useTranslation();
  const [isOpenMore, toggleOpenMore] = useToggle(false);
  const tenantId = props.tenantId ?? supportTenantId;
  const hasBilling = usePermission(EPermission.PERMISSION_VIEW_BILLING);

  const approvedSubjects = useGetApprovedSubjectsQuery(tenantId);
  const {channelLicenses, licensesTimeStamp} = useGetAllLicensesQuery(tenantId, {
    selectFromResult: ({data = [], isLoading, fulfilledTimeStamp}) => ({
      channelLicenses: data.filter(({channelType}) => LICENSE_CHANNELS.includes(channelType)),
      isLicensesLoading: isLoading,
      licensesTimeStamp: fulfilledTimeStamp,
    }),
  });

  const subjectLicenses = React.useMemo(() => {
    const preparedLicenses: TSubjectLicense[] = [];
    const vkSubject = approvedSubjects.find((subject) => subject.type === EChannelType.VK_NOTIFY);
    const okSubject = approvedSubjects.find((subject) => subject.type === EChannelType.OK_NOTIFY);
    const pushSubject = approvedSubjects.find((subject) => subject.type === EChannelType.PUSH);

    if (vkSubject) {
      preparedLicenses.push({
        id: -1,
        name: 'VkNotify',
        channelType: EChannelType.VK_NOTIFY,
        status: ELicenseStatus.ACTIVE,
      });
    }

    if (okSubject) {
      preparedLicenses.push({
        id: -2,
        name: 'OkNotify',
        channelType: EChannelType.OK_NOTIFY,
        status: ELicenseStatus.ACTIVE,
      });
    }

    if (pushSubject) {
      preparedLicenses.push({
        id: -3,
        name: 'Push',
        channelType: EChannelType.PUSH,
        status: ELicenseStatus.ACTIVE,
      });
    }

    return preparedLicenses;
  }, [approvedSubjects]);

  const combinedLicenses = React.useMemo(
    () => [...channelLicenses, ...subjectLicenses],

    [licensesTimeStamp, subjectLicenses],
  );

  const licenses = React.useMemo(
    () => (isOpenMore ? combinedLicenses : combinedLicenses.slice(0, DEFAULT_SHOWING_ITEMS)),
    [combinedLicenses, isOpenMore],
  );

  const hasShowAll = combinedLicenses.length > DEFAULT_SHOWING_ITEMS;

  const hasWhatsAppLicense = licenses.some(
    (license) => license.channelType === EChannelType.WHATSAPP,
  );
  const hasWhatsAppMauInfo = hasBilling && hasWhatsAppLicense;

  const tariffsBySubject = useTariffsBySubject(tenantId);
  const hasTariffs = !isEmpty(tariffsBySubject);

  const columnsWidth = React.useMemo(() => {
    const additionalColumns = [];

    if (hasTariffs) {
      additionalColumns.push(TABLE_TARIFF_CELL);
    }

    if (hasWhatsAppMauInfo) {
      additionalColumns.push(TABLE_WA_CELLS);
    }

    return [...TABLE_CELLS, ...additionalColumns];
  }, [hasTariffs, hasWhatsAppMauInfo]);

  return (
    <>
      <VerticalScrollTable columnsWidth={columnsWidth}>
        <TableHead noMargin>
          <S.Th>{t('Licenses:header.name')}</S.Th>
          <S.Th>{t('Licenses:header.subject')}</S.Th>
          <S.Th>{t('Licenses:header.status')}</S.Th>
          <S.Th>{t('Licenses:header.duration')}</S.Th>
          {hasTariffs && <S.Th>{t('Licenses:header.tariff')}</S.Th>}
          {hasWhatsAppMauInfo && (
            <>
              <S.Th>{t('Licenses:header.whatsAppMau')}</S.Th>
              <S.Th>
                {t('Licenses:header.whatsAppAmau')}
                <S.InlineHint
                  size="S"
                  hasFilledHeadIcon
                  hasMarginLeft
                  content={t('Licenses:header.whatsAppAmauHint')}
                />
              </S.Th>
            </>
          )}
        </TableHead>
        {licenses.map((item) => (
          <Item
            tenantId={tenantId}
            item={item}
            key={item.id}
            hasTariffs={hasTariffs}
            tariffsBySubject={tariffsBySubject}
            hasWhatsAppMauInfo={hasWhatsAppMauInfo}
          />
        ))}
      </VerticalScrollTable>
      {hasShowAll && (
        <S.More onClick={toggleOpenMore}>
          {t(`Licenses:${isOpenMore ? 'hide' : 'showAll'}`)}
          <S.ArrowIcon isOpen={isOpenMore} />
        </S.More>
      )}
    </>
  );
});

LicensesList.displayName = 'LicensesList';

export default LicensesList;
